import { css } from '@emotion/react';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import type { FC } from 'react';
import { Accordion } from '~/_shared/baseComponents/accordion';
import { FontAwesomeIcon } from '~/_shared/baseComponents/icon/fontAwesomeIcon.component';
import { RadioGroupComponent } from '~/_shared/baseComponents/radio/radioGroup.component';
import { ToggleComponent } from '~/_shared/baseComponents/toggle';
import { ModalComponent } from '~/_shared/components/modal/modal.component';
import { OverlayLoaderComponent } from '~/_shared/components/overlay/overlayLoader.component';
import { UserTwoFactorMode } from '~/_shared/constants/twoFactorMode.enum';
import type { Theme } from '~/_shared/themes/theme.model';
import { useTranslation } from '~/_shared/utils/hooks';
import { TwoFactorCommonComponent } from './twoFactorCommon.component';
import { TwoFactorGoogleComponent } from './twoFactorGoogle.component';

const ICON_SIZE = 18;

const rootStyle = css({
  fontSize: '14px',
  padding: 0,
});

const sectionStyle = css({
  gap: 20,
  cursor: 'default',
  padding: 20,
});

const radioGroupStyle = css({
  fontSize: 16,
  gap: 16,
});

const noteStyle = (theme: Theme) => ({
  display: 'flex',
  gap: 8,
  color: theme.textColors.info,
  fontSize: 14,
  marginBottom: 20,
  whiteSpace: 'pre-line',
});

const iconStyle = ({
  alignItems: 'center',
  display: 'flex',
  fontSize: ICON_SIZE,
  width: ICON_SIZE,
});

const headerStyle = css(sectionStyle, {
  alignItems: 'center',
  boxSizing: 'border-box',
  display: 'flex',
  justifyContent: 'space-between',
  width: '100%',
});

const googleWrapper = css({ marginTop: 12 });

type TwoFactorModalProps = Readonly<{
  isOpen: boolean;
  onClose: () => void;
  selectedTwoFactorMode: UserTwoFactorMode | null;
  setSelectedTwoFactorMode: (mode: UserTwoFactorMode | null) => void;
  isLoading: boolean;
  successMessage: string;
  errorMessage: string;
  isAlertOpen: boolean;
  qrCode: string | null;
  closeAlert: () => void;
  onTwoFactorGoogleSubmit: (code: string) => void;
  toggleTwoFactorCallBack: (toggle: boolean) => void;
  isTwoFactorOn: boolean;
}>;

export const TwoFactorModalComponent: FC<TwoFactorModalProps> = (props) => {
  const [t] = useTranslation();

  const items = [{
    label: <span>{t('Email')}</span>,
    value: UserTwoFactorMode.TwoFactorViaEmail,
    children: props.selectedTwoFactorMode === UserTwoFactorMode.TwoFactorViaEmail ? (
      <TwoFactorCommonComponent
        closeAlert={props.closeAlert}
        errorMessage={props.errorMessage}
        isAlertOpen={props.isAlertOpen}
        successMessage={props.successMessage}
      />
    ) : null,
  }, {
    label: <span>{t('Google Authenticator')}</span>,
    value: UserTwoFactorMode.TwoFactorViaGoogle,
    children: props.selectedTwoFactorMode === UserTwoFactorMode.TwoFactorViaGoogle ? (
      <div css={googleWrapper}>
        <TwoFactorGoogleComponent
          closeAlert={props.closeAlert}
          errorMessage={props.errorMessage}
          isAlertOpen={props.isAlertOpen}
          onTwoFactorGoogleSubmit={props.onTwoFactorGoogleSubmit}
          qrCode={props.qrCode}
          successMessage={props.successMessage}
        />
      </div>
    ) : null,
  }];

  return (
    <ModalComponent
      additionalContent={props.isLoading && <OverlayLoaderComponent />}
      caption={t('Two Factor Authentication')}
      contentStyle={rootStyle}
      isOpen={props.isOpen}
      onClose={props.onClose}
      skipCloseOnMapIdChange
    >
      <div>
        <div css={headerStyle}>
          <span>{t('2FA.enable')}</span>
          <ToggleComponent
            onChange={props.toggleTwoFactorCallBack}
            isOn={props.isTwoFactorOn}
          />
        </div>
        {props.isTwoFactorOn && (
          <Accordion>
            <Accordion.Item
              isLocked
              open
            >
              <Accordion.ItemHeading>
                {t('2FA.chooseMethod')}
              </Accordion.ItemHeading>
              <Accordion.Panel css={sectionStyle}>
                <div css={noteStyle}>
                  <div css={iconStyle}>
                    <FontAwesomeIcon icon={faInfoCircle} />
                  </div>
                  {t('2FA.withSSO')}
                </div>
                <RadioGroupComponent
                  css={radioGroupStyle}
                  items={items}
                  onValueChange={newValue => props.setSelectedTwoFactorMode(Object.values(UserTwoFactorMode).find(b => b === newValue) || null)}
                  selectedValue={props.selectedTwoFactorMode}
                />
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        )}
      </div>
    </ModalComponent>
  );
};
